import { useEffect, useState } from 'react'

interface CurrencyInfo {
  code: string
  symbol: string
  locale: string
  rate: number // calibrated rate vs USD (includes gateway margin)
}

// Rates are pre-calibrated to be slightly above what Hotmart charges,
// so the displayed price is always >= checkout price (pleasant surprise).
const CURRENCY_MAP: Record<string, CurrencyInfo> = {
  AR: { code: 'ARS', symbol: '$', locale: 'es-AR', rate: 1445 },
  CL: { code: 'CLP', symbol: '$', locale: 'es-CL', rate: 1144 },
  MX: { code: 'MXN', symbol: '$', locale: 'es-MX', rate: 22.5 },
  CO: { code: 'COP', symbol: '$', locale: 'es-CO', rate: 4325 },
  PE: { code: 'PEN', symbol: 'S/', locale: 'es-PE', rate: 4.52 },
  UY: { code: 'UYU', symbol: '$', locale: 'es-UY', rate: 48.17 },
  PY: { code: 'PYG', symbol: '₲', locale: 'es-PY', rate: 9031 },
  BO: { code: 'BOB', symbol: 'Bs', locale: 'es-BO', rate: 8.3 },
  CR: { code: 'CRC', symbol: '₡', locale: 'es-CR', rate: 614 },
  GT: { code: 'GTQ', symbol: 'Q', locale: 'es-GT', rate: 9.39 },
  HN: { code: 'HNL', symbol: 'L', locale: 'es-HN', rate: 30.1 },
  NI: { code: 'NIO', symbol: 'C$', locale: 'es-NI', rate: 43.95 },
  DO: { code: 'DOP', symbol: 'RD$', locale: 'es-DO', rate: 69.84 },
  ES: { code: 'EUR', symbol: '€', locale: 'es-ES', rate: 1.11 },
  EC: { code: 'USD', symbol: '$', locale: 'es-EC', rate: 1 },
  PA: { code: 'USD', symbol: '$', locale: 'es-PA', rate: 1 },
  SV: { code: 'USD', symbol: '$', locale: 'es-SV', rate: 1 },
  VE: { code: 'USD', symbol: '$', locale: 'es-VE', rate: 1 },
  BR: { code: 'BRL', symbol: 'R$', locale: 'pt-BR', rate: 6.15 },
  US: { code: 'USD', symbol: 'US$', locale: 'en-US', rate: 1 },
}

const DEFAULT_CURRENCY: CurrencyInfo = {
  code: 'USD',
  symbol: 'US$',
  locale: 'en-US',
  rate: 1,
}

/**
 * Rounds a price to the nearest "charm price" for marketing appeal.
 * Rules by magnitude:
 *   >= 100,000 → round to thousands - 100  (e.g. 203,145 → 199,900)
 *   >= 10,000  → round to thousands - 100  (e.g. 16,012 → 15,900)
 *   >= 1,000   → round to hundreds - 10    (e.g. 1,523 → 1,490)
 *   >= 100     → round to tens - 1         (e.g. 316 → 299)
 *   >= 10      → floor - 0.01              (e.g. 63.2 → 62.99)
 *   < 10       → floor + 0.99              (e.g. 6.3 → 6.99)
 */
function toCharmPrice(amount: number): number {
  if (amount >= 100000) {
    const thousands = Math.floor(amount / 1000) * 1000
    return thousands - 100
  }
  if (amount >= 10000) {
    const thousands = Math.floor(amount / 1000) * 1000
    return thousands - 100
  }
  if (amount >= 1000) {
    const hundreds = Math.floor(amount / 100) * 100
    return hundreds - 10
  }
  if (amount >= 100) {
    const tens = Math.floor(amount / 10) * 10
    return tens - 1
  }
  if (amount >= 10) {
    return Math.floor(amount) - 0.01
  }
  // < 10
  return Math.floor(amount) + 0.99
}

/** Convert a 2-letter country code to its flag emoji */
function countryToFlag(code: string): string {
  return code
    .toUpperCase()
    .replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)))
}

interface LocalCurrencyResult {
  formatPrice: (usdAmount: number) => string
  formatPriceCompact: (usdAmount: number) => string
  currencyCode: string
  currencySymbol: string
  countryCode: string
  countryFlag: string
  isUSD: boolean
  isLoading: boolean
}

export function useLocalCurrency(): LocalCurrencyResult {
  const [currency, setCurrency] = useState<CurrencyInfo>(DEFAULT_CURRENCY)
  const [isLoading, setIsLoading] = useState(true)
  const [countryCode, setCountryCode] = useState('US')

  useEffect(() => {
    const cached = sessionStorage.getItem('mp_visitor_country')
    if (cached) {
      const info = CURRENCY_MAP[cached] || DEFAULT_CURRENCY
      setCurrency(info)
      setCountryCode(cached)
      setIsLoading(false)
      return
    }

    const detectCountry = async () => {
      try {
        const res = await fetch('https://ipapi.co/json/', {
          signal: AbortSignal.timeout(4000),
        })
        if (!res.ok) throw new Error('API error')
        const data = await res.json()
        const code = data.country_code || 'US'
        sessionStorage.setItem('mp_visitor_country', code)
        const info = CURRENCY_MAP[code] || DEFAULT_CURRENCY
        setCurrency(info)
        setCountryCode(code)
      } catch {
        // Fallback: infer country from browser locale
        const browserLocale = navigator.language || 'en-US'
        const localeCountry = browserLocale.split('-')[1]?.toUpperCase()
        if (localeCountry && CURRENCY_MAP[localeCountry]) {
          setCurrency(CURRENCY_MAP[localeCountry])
          setCountryCode(localeCountry)
          sessionStorage.setItem('mp_visitor_country', localeCountry)
        }
      } finally {
        setIsLoading(false)
      }
    }

    detectCountry()
  }, [])

  const isUSD = currency.code === 'USD'

  const formatPrice = (usdAmount: number): string => {
    const raw = usdAmount * currency.rate
    // Apply charm pricing only for non-USD
    const converted = isUSD ? raw : toCharmPrice(raw)
    const usesDecimals = currency.rate < 100
    try {
      const formatted = new Intl.NumberFormat(currency.locale, {
        style: 'currency',
        currency: currency.code,
        minimumFractionDigits: usesDecimals ? 2 : 0,
        maximumFractionDigits: usesDecimals ? 2 : 0,
      }).format(converted)
      // Append currency code for non-USD to disambiguate (e.g. "$15.900 CLP")
      return isUSD ? formatted : `${formatted} ${currency.code}`
    } catch {
      const num = converted.toFixed(usesDecimals ? 2 : 0)
      return isUSD
        ? `${currency.symbol}${num}`
        : `${currency.symbol}${num} ${currency.code}`
    }
  }

  const formatPriceCompact = (usdAmount: number): string => {
    const raw = usdAmount * currency.rate
    const converted = isUSD ? raw : toCharmPrice(raw)
    let formatted: string
    if (currency.rate >= 100) {
      if (converted >= 10000) {
        const thousands = converted / 1000
        formatted = `${currency.symbol}${thousands.toFixed(1)}k`
      } else {
        formatted = `${currency.symbol}${Math.round(converted).toLocaleString(currency.locale)}`
      }
    } else {
      formatted = `${currency.symbol}${converted.toFixed(2)}`
    }
    return isUSD ? formatted : `${formatted} ${currency.code}`
  }

  return {
    formatPrice,
    formatPriceCompact,
    currencyCode: currency.code,
    currencySymbol: currency.symbol,
    countryCode,
    countryFlag: countryToFlag(countryCode),
    isUSD,
    isLoading,
  }
}
